import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * <ig-metric> — a HUD-style metric tile for the Overview "Ops Deck".
 *
 * Renders an animated count-up value with a monospace instrument label and an
 * optional semantic state. Light-DOM styling comes from styles/overview.css so
 * it inherits the app's design tokens; the count-up and reduced-motion handling
 * live here because they need the element lifecycle.
 */
@customElement("ig-metric")
export class MetricTile extends LitElement {
  // Render into light DOM so the shared token-based CSS applies.
  protected createRenderRoot() {
    return this;
  }

  /** Numeric value to display; when set, it counts up from the previous value. */
  @property({ type: Number }) value: number | null = null;
  /** Text shown when value is null (e.g. "n/a", "Enabled"). */
  @property({ type: String }) text: string | null = null;
  @property({ type: String }) label = "";
  @property({ type: String }) unit = "";
  /** Semantic state → colors the value + endpoint dot. */
  @property({ type: String }) state: "" | "ok" | "warn" | "danger" | "info" | "accent" = "";
  /** Cluster tint, purely visual grouping. */
  @property({ type: String }) tint: "" | "activity" | "config" | "scale" = "";

  private displayed = 0;
  private raf = 0;

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.raf) {
      cancelAnimationFrame(this.raf);
    }
  }

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has("value") && this.value != null) {
      this.animateTo(this.value);
    }
  }

  private animateTo(target: number) {
    const reduce =
      typeof matchMedia === "function" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (this.raf) {
      cancelAnimationFrame(this.raf);
    }
    if (reduce || target === this.displayed) {
      this.displayed = target;
      this.requestUpdate();
      return;
    }
    const from = this.displayed;
    const delta = target - from;
    const dur = 650;
    let start = 0;
    const step = (t: number) => {
      if (!start) {
        start = t;
      }
      const p = Math.min(1, (t - start) / dur);
      // easeOutExpo — snappy, instrument-like settle
      const e = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      this.displayed = from + delta * e;
      this.requestUpdate();
      if (p < 1) {
        this.raf = requestAnimationFrame(step);
      }
    };
    this.raf = requestAnimationFrame(step);
  }

  render() {
    const shown =
      this.value != null
        ? Math.round(this.displayed).toLocaleString()
        : (this.text ?? "n/a");
    const cls = [
      "ig-metric",
      this.state ? `is-${this.state}` : "",
      this.tint ? `tint-${this.tint}` : "",
    ]
      .filter(Boolean)
      .join(" ");
    return html`
      <div class=${cls}>
        <div class="ig-metric__top">
          <span class="ig-metric__dot" aria-hidden="true"></span>
          <span class="ig-metric__label">${this.label}</span>
        </div>
        <div class="ig-metric__value">
          <span class="ig-metric__num">${shown}</span>
          ${this.unit ? html`<span class="ig-metric__unit">${this.unit}</span>` : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ig-metric": MetricTile;
  }
}
